/** Street + locality fields used for geocoding and display. */
export type AddressParts = {
  address?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;
};

/**
 * Joins address parts into a single comma-separated line for display or geocoding.
 * @param parts - Street, city, postcode, and country
 * @returns Formatted address, or empty string when no parts are set
 */
export function formatFullAddress(parts: AddressParts): string {
  return [parts.address, parts.city, parts.postcode, parts.country]
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter(Boolean)
    .join(', ');
}

/**
 * Whether all address parts required for nearby search are present.
 * @param parts - Street, city, postcode, and country
 * @returns `true` when every part is a non-empty string
 */
export function hasCompleteAddress(parts: AddressParts): boolean {
  return Boolean(
    parts.address?.trim() &&
      parts.city?.trim() &&
      parts.postcode?.trim() &&
      parts.country?.trim()
  );
}

/**
 * Converts text to a URL-safe slug for clinic identifiers.
 * @param text - Source string, typically a clinic name
 * @returns Lowercase slug with spaces replaced by hyphens
 */
export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-'); // Replace multiple - with single -
}

/**
 * Checks whether two dates fall on the same UTC calendar day.
 * @param date1 - First date value
 * @param date2 - Second date value
 * @returns True when year, month, and day match in UTC
 */
export function isSameDay(date1: Date | string, date2: Date | string): boolean {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate()
  );
}

/**
 * Formats a date as `YYYY-MM-DD` in local time.
 * @param date - Date value to format
 * @returns ISO date string without time component
 */
export function formatDateOnly(date: Date | string): string {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Generates contiguous time slot windows between a start and end time.
 * @param startTime - Range start in `HH:MM` or `HH:MM:SS` format
 * @param endTime - Range end in `HH:MM` or `HH:MM:SS` format
 * @param slotDurationMinutes - Length of each slot in minutes
 * @returns Array of slot objects with start and end times
 */
export function generateTimeSlots(
  startTime: string,
  endTime: string,
  slotDurationMinutes: number
): { startTime: string; endTime: string }[] {
  const slots: { startTime: string; endTime: string }[] = [];

  const parseTimeToMinutes = (timeStr: string): number => {
    const parts = timeStr.split(':');
    return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
  };

  const formatMinutesToTime = (totalMin: number): string => {
    const hours = String(Math.floor(totalMin / 60)).padStart(2, '0');
    const mins = String(totalMin % 60).padStart(2, '0');
    return `${hours}:${mins}`;
  };

  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);

  let currentMinutes = startMinutes;
  while (currentMinutes + slotDurationMinutes <= endMinutes) {
    const nextMinutes = currentMinutes + slotDurationMinutes;
    slots.push({
      startTime: formatMinutesToTime(currentMinutes),
      endTime: formatMinutesToTime(nextMinutes),
    });
    currentMinutes = nextMinutes;
  }

  return slots;
}

/**
 * Computes pagination metadata for a list response.
 * @param totalItems - Total number of records across all pages
 * @param page - Current one-based page index
 * @param limit - Maximum items per page
 * @returns Page, limit, totals, and next/previous flags
 */
export function getPaginationMetadata(totalItems: number, page: number, limit: number) {
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  return {
    page,
    limit,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1,
  };
}

const LIST_QUERY_JSON_KEYS = new Set([
  'specificSearchFields',
  'specificValueFields',
  'specificRangeFields',
  'specificOrFields',
  'specificNotFields',
  'specificNumericFields',
]);

/**
 * Serializes Eaze-style list query options into flat HTTP query params.
 * Objects under `specific*Fields` are JSON-stringified; `itemsPerPage` maps to `limit`.
 */
export function toListQueryParams(
  opts: Record<string, unknown> | null | undefined
): Record<string, string> {
  if (!opts) return {};

  const params: Record<string, string> = {};
  const limit = opts['limit'] ?? opts['itemsPerPage'];

  for (const [key, value] of Object.entries(opts)) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'itemsPerPage') continue;

    if (key === 'limit' || (key === 'page' && limit !== undefined)) {
      // handled below for limit; page falls through
    }

    if (LIST_QUERY_JSON_KEYS.has(key) && typeof value === 'object') {
      params[key] = JSON.stringify(value);
      continue;
    }

    if (Array.isArray(value)) {
      params[key] = value.join(',');
      continue;
    }

    if (typeof value === 'boolean' || typeof value === 'number') {
      params[key] = String(value);
      continue;
    }

    if (typeof value === 'string') {
      params[key] = value;
    }
  }

  if (limit !== undefined && limit !== null && limit !== '') {
    params['limit'] = String(limit);
  }

  return params;
}
